/**
 * Cognito callback handling (phase 2): read code/state, validate state, exchange
 * code+PKCE for tokens, validate the id_token, establish the session, and clear
 * one-time PKCE/state/nonce values.
 */

import { STORAGE_KEYS } from './constants';
import { exchangeCodeForTokens } from './token';
import { saveSession } from './session';
import { validateIdToken } from './validate';
import { startTokenRefreshScheduler } from './refresh';

/** Read OAuth callback parameters from the current URL. */
export const getCallbackParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    code: params.get('code'),
    state: params.get('state'),
    error: params.get('error'),
    errorDescription: params.get('error_description'),
  };
};

const clearTransientValues = () => {
  sessionStorage.removeItem(STORAGE_KEYS.STATE);
  sessionStorage.removeItem(STORAGE_KEYS.NONCE);
  sessionStorage.removeItem(STORAGE_KEYS.CODE_VERIFIER);
};

/** Handle the Cognito callback: validate state, exchange code, validate id_token, start session. */
export const handleCognitoCallback = async () => {
  const { code, state, error, errorDescription } = getCallbackParams();

  if (error) {
    throw new Error(errorDescription || error);
  }
  if (!code) {
    throw new Error('Missing authorization code in callback');
  }

  const storedState = sessionStorage.getItem(STORAGE_KEYS.STATE);
  if (!state || state !== storedState) {
    throw new Error('Invalid state parameter (possible CSRF)');
  }

  const codeVerifier = sessionStorage.getItem(STORAGE_KEYS.CODE_VERIFIER);
  if (!codeVerifier) {
    throw new Error('Missing PKCE code_verifier');
  }

  const tokens = await exchangeCodeForTokens({ code, codeVerifier });

  // Validate the id_token: signature, iss, aud, exp, nonce, token_use.
  const storedNonce = sessionStorage.getItem(STORAGE_KEYS.NONCE);
  const claims = await validateIdToken(tokens.id_token, { nonce: storedNonce });

  saveSession(tokens);
  clearTransientValues();

  // Keep the short-lived access_token fresh; no-op when no refresh_token is enabled.
  startTokenRefreshScheduler();

  return { tokens, claims };
};

export default handleCognitoCallback;
